// deno-lint-ignore-file no-unused-vars
// Zakładka „Statystyki” (spec/statystyki.md): dodane / wykonane / anulowane / usunięte w okresach —
// kontrolki, kafelki sum, grupowany wykres słupkowy (inline SVG, bez bibliotek) i tabela.
import { h, Fragment } from '../jsx-runtime.ts';
import { StatsAPI, type StatsBucket, type StatsResult } from '../api.ts';
import { showToast } from './tmk-toast.tsx';
import { SERIES, type RangePreset, presetRange, presetForBucket, periodLabel, labelIndices, yScale } from '../stats-core.ts';

const SVG_NS = 'http://www.w3.org/2000/svg';
function svg(tag: string, attrs: Record<string, string | number> = {}, text?: string): SVGElement {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  if (text !== undefined) el.textContent = text;
  return el;
}

class TmkStats extends HTMLElement {
  private bucket: StatsBucket = 'day';
  private preset: RangePreset = '30d';
  private from = '';
  private to = '';
  private data: StatsResult | null = null;
  private seq = 0;
  /** Filtr tagów z sidebara (spec/statystyki.md → Filtr tagów w sidebarze); render() z nowym filtrem zachowuje kubełek i zakres. */
  private tagFilter: { tag_groups?: string; tag_exclude?: string } = {};

  async render(opts: { tag_groups?: string; tag_exclude?: string } = {}) {
    this.tagFilter = { tag_groups: opts.tag_groups, tag_exclude: opts.tag_exclude };
    if (!this.from) { const r = presetRange(this.preset); this.from = r.from; this.to = r.to; }
    if (this.querySelector('.stats-controls')) { this.refresh(); return; }
    this.innerHTML = '';
    this.appendChild(this.renderControls());
    const body = <div className="stats-body">Ładowanie…</div> as HTMLElement;
    this.appendChild(body);
    await this.load(body);
  }

  private async load(body: HTMLElement) {
    const my = ++this.seq;
    try {
      const data = await StatsAPI.summary({ from: this.from, to: this.to, bucket: this.bucket, ...this.tagFilter });
      if (my !== this.seq) return;
      this.data = data;
      body.replaceChildren(this.renderTotals(data), this.renderChart(data), this.renderTable(data));
    } catch (err: unknown) {
      if (my !== this.seq) return;
      body.textContent = 'Nie udało się pobrać statystyk: ' + (err as Error).message;
      showToast('Błąd statystyk: ' + (err as Error).message, 'error');
    }
  }

  private refresh() {
    const body = this.querySelector('.stats-body') as HTMLElement | null;
    const controls = this.querySelector('.stats-controls');
    if (controls) controls.replaceWith(this.renderControls());
    if (body) { body.textContent = 'Ładowanie…'; this.load(body); }
  }

  private setBucket(b: StatsBucket) {
    this.bucket = b;
    if (this.preset !== 'custom') { this.preset = presetForBucket(b); const r = presetRange(this.preset); this.from = r.from; this.to = r.to; }
    this.refresh();
  }

  private setPreset(p: RangePreset) {
    this.preset = p;
    if (p !== 'custom') { const r = presetRange(p); this.from = r.from; this.to = r.to; }
    this.refresh();
  }

  private renderControls(): HTMLElement {
    const bucketBtn = (b: StatsBucket, label: string) =>
      <button type="button" className={'btn btn-ghost stats-seg' + (this.bucket === b ? ' active' : '')} data-bucket={b}
        onClick={() => this.setBucket(b)}>{label}</button>;
    const presetBtn = (p: RangePreset, label: string) =>
      <button type="button" className={'btn btn-ghost stats-seg' + (this.preset === p ? ' active' : '')} data-preset={p}
        onClick={() => this.setPreset(p)}>{label}</button>;
    const fromInput = <input type="date" className="stats-date" value={this.from} max={this.to}
      onChange={(e: Event) => { this.from = (e.target as HTMLInputElement).value; this.preset = 'custom'; this.refresh(); }} /> as HTMLInputElement;
    const toInput = <input type="date" className="stats-date" value={this.to} min={this.from}
      onChange={(e: Event) => { this.to = (e.target as HTMLInputElement).value; this.preset = 'custom'; this.refresh(); }} /> as HTMLInputElement;
    const filtered = !!(this.tagFilter.tag_groups || this.tagFilter.tag_exclude);
    return <div className="stats-controls">
      {filtered ? <span className="stats-filter-note" title="Statystyki liczone dla zadań spełniających warunki tagów z sidebara">🏷 filtr tagów</span> : null}
      <div className="stats-seg-group" role="group" aria-label="Okres">
        {bucketBtn('day', 'Dzień')}{bucketBtn('week', 'Tydzień')}{bucketBtn('month', 'Miesiąc')}
      </div>
      <div className="stats-seg-group" role="group" aria-label="Zakres">
        {presetBtn('30d', '30 dni')}{presetBtn('90d', '90 dni')}{presetBtn('12m', '12 mies.')}
      </div>
      <label className={'stats-custom' + (this.preset === 'custom' ? ' active' : '')}>
        <span>od</span>{fromInput}<span>do</span>{toInput}
      </label>
    </div> as HTMLElement;
  }

  private renderTotals(data: StatsResult): HTMLElement {
    return <div className="stats-totals">
      {SERIES.map((s) => <div className="stats-tile" data-series={s.key}>
        <span className="stats-tile-swatch" style={{ background: s.color }} />
        <span className="stats-tile-label">{s.label}</span>
        <span className="stats-tile-value">{data.totals[s.key]}</span>
      </div>)}
    </div> as HTMLElement;
  }

  private renderChart(data: StatsResult): HTMLElement {
    const wrap = <div className="stats-chart" /> as HTMLElement;
    const rows = data.rows;
    const n = rows.length;
    // Szerokość viewBox = realna szerokość komponentu, żeby tekst osi miał ~11px także na telefonie
    const W = Math.max(320, (this.clientWidth || 900) - 26), H = Math.round(W / 3) + 60;
    const padL = 36, padR = 8, padT = 12, padB = 28;
    const plotW = W - padL - padR, plotH = H - padT - padB;
    const max = Math.max(0, ...rows.flatMap((r) => SERIES.map((s) => r[s.key])));
    const { step, yMax } = yScale(max);
    const root = svg('svg', { viewBox: `0 0 ${W} ${H}`, class: 'stats-svg', role: 'img', 'aria-label': 'Wykres: zadania dodane, wykonane, anulowane i usunięte w okresach' });

    // Siatka pozioma + oś Y (całkowite kroki)
    for (let v = 0; v <= yMax; v += step) {
      const y = padT + plotH - (v / yMax) * plotH;
      root.appendChild(svg('line', { x1: padL, x2: W - padR, y1: y, y2: y, class: 'stats-grid' }));
      root.appendChild(svg('text', { x: padL - 6, y: y + 4, class: 'stats-axis-text', 'text-anchor': 'end' }, String(v)));
    }
    root.appendChild(svg('line', { x1: padL, x2: W - padR, y1: padT + plotH, y2: padT + plotH, class: 'stats-baseline' }));

    if (n > 0) {
      const groupW = plotW / n;
      const gap = 2;                                // odstęp między słupkami (2px powierzchni)
      const inner = Math.max(groupW * 0.72, 4);
      const barW = Math.max((inner - gap * (SERIES.length - 1)) / SERIES.length, 1);
      const labels = labelIndices(n, Math.floor(plotW / 48));
      const tooltip = <div className="stats-tooltip" hidden /> as HTMLElement;
      wrap.appendChild(tooltip);

      rows.forEach((r, i) => {
        const gx = padL + i * groupW + (groupW - inner) / 2;
        const g = svg('g', { class: 'stats-group', 'data-period': r.period });
        // Obszar trafienia hover/tap większy niż słupki
        g.appendChild(svg('rect', { x: padL + i * groupW, y: padT, width: groupW, height: plotH, class: 'stats-hit' }));
        SERIES.forEach((s, si) => {
          const v = r[s.key];
          if (v <= 0) return;
          const hgt = Math.max((v / yMax) * plotH, 2);
          const x = gx + si * (barW + gap);
          const y = padT + plotH - hgt;
          const rx = Math.min(4, barW / 2);
          // Zaokrąglony tylko górny koniec: path z łukami u góry, płaska podstawa
          const d = `M${x},${padT + plotH} V${y + rx} A${rx},${rx} 0 0 1 ${x + rx},${y} H${x + barW - rx} A${rx},${rx} 0 0 1 ${x + barW},${y + rx} V${padT + plotH} Z`;
          g.appendChild(svg('path', { d, fill: s.color, class: 'stats-bar', 'data-series': s.key }));
        });
        if (labels.has(i)) {
          root.appendChild(svg('text', { x: padL + i * groupW + groupW / 2, y: H - 8, class: 'stats-axis-text', 'text-anchor': 'middle' }, periodLabel(r.period, data.bucket)));
        }
        const show = () => {
          tooltip.replaceChildren(
            <div className="stats-tooltip-title">{periodLabel(r.period, data.bucket)}{data.bucket === 'week' ? ' (tydzień od)' : ''}</div> as HTMLElement,
            ...SERIES.map((s) => <div className="stats-tooltip-row">
              <span className="stats-tile-swatch" style={{ background: s.color }} /><span>{s.label}</span><b>{r[s.key]}</b>
            </div> as HTMLElement),
          );
          tooltip.hidden = false;
          const pct = (padL + i * groupW + groupW / 2) / W;
          tooltip.style.left = `${pct * 100}%`;
          tooltip.classList.toggle('flip', pct > 0.75);
        };
        g.addEventListener('mouseenter', show);
        g.addEventListener('click', show);
        g.addEventListener('mouseleave', () => { tooltip.hidden = true; });
        root.appendChild(g);
      });
    }
    wrap.appendChild(root);

    // Legenda (zawsze — cztery serie)
    wrap.appendChild(<div className="stats-legend">
      {SERIES.map((s) => <span className="stats-legend-item"><span className="stats-tile-swatch" style={{ background: s.color }} />{s.label}</span>)}
    </div> as HTMLElement);

    const empty = rows.every((r) => SERIES.every((s) => r[s.key] === 0));
    if (empty) wrap.appendChild(<div className="stats-empty">Brak zdarzeń w tym okresie</div> as HTMLElement);
    return wrap;
  }

  private renderTable(data: StatsResult): HTMLElement {
    return <div className="stats-table-wrap">
      <table className="stats-table">
        <thead><tr><th>Okres</th>{SERIES.map((s) => <th>{s.label}</th>)}</tr></thead>
        <tbody>
          {data.rows.map((r) => <tr data-period={r.period}>
            <td>{periodLabel(r.period, data.bucket)}</td>{SERIES.map((s) => <td>{r[s.key]}</td>)}
          </tr>)}
        </tbody>
        <tfoot><tr><th>Razem</th>{SERIES.map((s) => <th>{data.totals[s.key]}</th>)}</tr></tfoot>
      </table>
    </div> as HTMLElement;
  }
}

customElements.define('tmk-stats', TmkStats);

export { TmkStats };
